/**
 * Wat er over een zoekopdracht te zeggen valt zonder ernaar te kijken: hoe lang
 * geleden de laatste ronde was, of hij stilstaat, en of hij een andere
 * zoekopdracht in de weg zit. Pure rekenkunde, los van de tabel, zodat het te
 * testen is zonder een component te tekenen.
 */
import {
  BLOKKADESOORT_LABELS,
  FUNDA_DAGPLAFOND_WAARSCHUW_BOVEN,
  type FundaBlokkade,
  type FundaRust,
  formatAantal,
  formatDatumTijd,
  type Gebied,
  overlappendeGebieden,
  RONDESOORT_LABELS,
  type Zoekopdracht,
} from '@mamimap/shared';

/**
 * Eén regel over de globale stand van de vanger (MA-28): de Funda-rust na een
 * blokkade, als die loopt, en het dagtotaal tegen het plafond. Tekst, geen
 * kader: het is een stand, geen fout.
 *
 * `Funda-rust tot 11 okt 2026, 14:32 · vandaag 23 van 50 pagina's`
 * `Vandaag 23 van 50 pagina's` als er geen rust loopt
 * `Vandaag 50 van 50 pagina's, tot morgen niets meer` als het plafond vol is
 */
export function fundaRustRegel(stand: FundaRust, nu: Date = new Date()): string {
  const delen: string[] = [];
  const rustLoopt = stand.rustTot !== null && new Date(stand.rustTot) > nu;
  if (rustLoopt) delen.push(`Funda-rust tot ${formatDatumTijd(stand.rustTot)}`);

  const vol = stand.paginasVandaag >= stand.dagplafond;
  const dag = `${rustLoopt ? 'vandaag' : 'Vandaag'} ${formatAantal(stand.paginasVandaag)} van ${formatAantal(stand.dagplafond)} pagina's`;
  delen.push(vol && !rustLoopt ? `${dag}, tot morgen niets meer` : dag);

  if (stand.blokkades.length >= 2) {
    delen.push(`${stand.blokkades.length} blokkades in dertig dagen`);
  }
  return delen.join(' · ');
}

/** "12:15 en 19:30", "12:15, 16:00 en 19:30". */
export function tijdenTekst(tijden: readonly string[]): string {
  if (tijden.length <= 1) return tijden[0] ?? '';
  return `${tijden.slice(0, -1).join(', ')} en ${tijden.at(-1)}`;
}

/**
 * "Om 12:15 en 19:30", of bij een vaste reeks (MA-62) "Elk uur van 08:00 tot
 * 22:00": vijftien tijden opsommen leest niemand.
 */
export function tijdenZin(tijden: readonly string[]): string {
  const stap = vasteStap(tijden);
  if (stap === null) return `Om ${tijdenTekst(tijden)}`;
  const elke =
    stap === 60 ? 'Elk uur' : stap % 60 === 0 ? `Elke ${stap / 60} uur` : `Elke ${stap} minuten`;
  return `${elke} van ${tijden[0]} tot ${tijden.at(-1)}`;
}

/** De tussenpoos in minuten als drie of meer tijden steeds even ver uit elkaar liggen, anders null. */
function vasteStap(tijden: readonly string[]): number | null {
  if (tijden.length < 3) return null;
  const minuten = tijden.map((tijd) => {
    const [uur = 0, minuut = 0] = tijd.split(':').map(Number);
    return uur * 60 + minuut;
  });
  const stap = (minuten[1] ?? 0) - (minuten[0] ?? 0);
  if (stap <= 0) return null;
  return minuten.every((m, i) => i === 0 || m - (minuten[i - 1] ?? 0) === stap) ? stap : null;
}

/**
 * De regel over de vaste tijden (MA-46).
 *
 * `Om 12:15 en 19:30, 35 pagina's per keer · nu bezig: 14 van 35`
 * `Om 12:15 en 19:30, 35 pagina's per keer · volgende keer 19:30`
 */
export function vensterRegel(stand: FundaRust): string {
  const vast = `${tijdenZin(stand.venster.tijden)}, ${formatAantal(stand.venster.paginas)} pagina's per keer`;
  if (stand.vensterNu) {
    const klaar = stand.paginasDezeKeer >= stand.venster.paginas;
    return `${vast} · ${klaar ? 'deze keer klaar' : 'nu bezig'}: ${formatAantal(stand.paginasDezeKeer)} van ${formatAantal(stand.venster.paginas)}`;
  }
  return stand.volgendeTijd ? `${vast} · volgende keer ${stand.volgendeTijd}` : vast;
}

/** "12:15, 19:30" of "12.15 19.30" uit een tekstveld; null als er een tijd niet klopt. */
export function leesTijden(tekst: string): string[] | null {
  const delen = tekst
    .split(/[\s,;]+/)
    .map((deel) => deel.trim().replace('.', ':'))
    .filter((deel) => deel !== '');
  if (delen.length === 0) return null;
  const tijden: string[] = [];
  for (const deel of delen) {
    const treffer = /^(\d{1,2}):(\d{2})$/.exec(deel);
    if (!treffer) return null;
    const uur = Number(treffer[1]);
    const minuut = Number(treffer[2]);
    if (uur > 23 || minuut > 59) return null;
    tijden.push(`${String(uur).padStart(2, '0')}:${String(minuut).padStart(2, '0')}`);
  }
  return [...new Set(tijden)].sort();
}

/**
 * De cijfers van één blokkade als korte tekst voor in het log (MA-46).
 *
 * `pagina 2 · 14 in het laatste uur · 38 in drie uur · 41 vandaag (7e ronde) · 12 deze keer`
 */
export function blokkadeCijfers(blokkade: FundaBlokkade): string {
  const delen: string[] = [];
  if (blokkade.pagina !== null) delen.push(`pagina ${formatAantal(blokkade.pagina)}`);
  delen.push(`${formatAantal(blokkade.paginasUur)} in het laatste uur`);
  delen.push(`${formatAantal(blokkade.paginasDrieUur)} in drie uur`);
  delen.push(
    `${formatAantal(blokkade.paginasVandaag)} vandaag (${formatAantal(blokkade.rondesVandaag)}e ronde)`,
  );
  if (blokkade.paginasDezeKeer !== null) {
    delen.push(`${formatAantal(blokkade.paginasDezeKeer)} deze keer`);
  }
  return delen.join(' · ');
}

/** Wat Funda liet zien, en of er een mens op wacht. */
export function blokkadeSoortTekst(blokkade: FundaBlokkade): string {
  const soort = BLOKKADESOORT_LABELS[blokkade.soort];
  return blokkade.wachtOpMens ? `${soort}, wacht op mens` : soort;
}

/** De rust die erop volgde, of niets. */
export function blokkadeGevolg(blokkade: FundaBlokkade): string {
  if (blokkade.rustTot === null) return blokkade.wachtOpMens ? 'nog geen rust' : '';
  const trap = blokkade.trap === null ? '' : `${blokkade.trap}e blokkade, `;
  return `${trap}rust tot ${formatDatumTijd(blokkade.rustTot)}`;
}

/**
 * De nuchtere regel bij een hoog dagplafond (MA-32). Boven honderd pagina's
 * zegt het scherm wat er op 9 oktober 2026 gebeurde; geen rood en geen kader,
 * want het is een waarneming en geen fout. Wie het plafond hoog zet mag dat
 * weten, en dan zelf kiezen.
 */
export function dagplafondWaarschuwing(dagplafond: number): string | null {
  if (dagplafond <= FUNDA_DAGPLAFOND_WAARSCHUW_BOVEN) return null;
  return "Op 9 oktober kwam de blokkade na 60 pagina's in een kwartier; spreiding telt zwaarder dan het totaal.";
}

/**
 * `3 uur geleden`, `over 2 dagen`, `zojuist`, `nog nooit`.
 *
 * Afgerond, niet precies: "127 minuten geleden" leest niemand. Tot een uur in
 * minuten, tot twee dagen in uren, daarna in dagen.
 */
export function relatieveTijd(iso: string | null | undefined, nu: Date = new Date()): string {
  if (!iso) return 'nog nooit';
  const tijd = new Date(iso).getTime();
  if (Number.isNaN(tijd)) return 'nog nooit';

  const verschil = nu.getTime() - tijd;
  const toekomst = verschil < 0;
  const minuten = Math.round(Math.abs(verschil) / 60_000);

  if (minuten < 1) return 'zojuist';

  let kern: string;
  if (minuten < 60) kern = `${minuten} min`;
  else if (minuten < 48 * 60) {
    const uren = Math.round(minuten / 60);
    return toekomst ? `over ${uren} uur` : `${uren} uur geleden`;
  } else {
    const dagen = Math.round(minuten / 1440);
    kern = `${dagen} dagen`;
  }
  return toekomst ? `over ${kern}` : `${kern} geleden`;
}

export type StandToon = 'rustig' | 'let-op' | 'uit';
export type Stand = { toon: StandToon; tekst: string };

/** Hoeveel keer het eigen interval een zoekopdracht stil mag staan voor het telt. */
const STILTE_FACTOR = 2;

/**
 * Eén regel over de stand van een zoekopdracht, voor de kolom Stand.
 *
 * De volgorde is de volgorde van wat je moet weten: staat hij uit, dan is de
 * rest niet van belang; staat er uitstel op, dan zegt dat waarom er niets
 * gebeurt; is hij te lang stil, dan is dát het nieuws; en pas daarna hoe de
 * laatste ronde afliep.
 */
export function zoekopdrachtStand(zoekopdracht: Zoekopdracht, nu: Date = new Date()): Stand {
  if (!zoekopdracht.actief) return { toon: 'uit', tekst: 'uit' };

  const run = zoekopdracht.laatsteRun;

  if (zoekopdracht.uitgesteldTot && new Date(zoekopdracht.uitgesteldTot) > nu) {
    const reden = run && run.soort !== 'goed' ? RONDESOORT_LABELS[run.soort] : 'wacht';
    return {
      toon: 'let-op',
      tekst: `${reden}, opnieuw ${relatieveTijd(zoekopdracht.uitgesteldTot, nu)}`,
    };
  }

  const grens = zoekopdracht.intervalMinuten * STILTE_FACTOR * 60_000;
  const referentie = zoekopdracht.laatsteRunOp ?? zoekopdracht.aangemaaktOp;
  if (nu.getTime() - new Date(referentie).getTime() > grens) {
    return {
      toon: 'let-op',
      tekst: zoekopdracht.laatsteRunOp
        ? `stil, laatste ronde ${relatieveTijd(zoekopdracht.laatsteRunOp, nu)}`
        : 'nog niet gelopen',
    };
  }

  if (run && run.soort !== 'goed') return { toon: 'let-op', tekst: RONDESOORT_LABELS[run.soort] };
  if (!run) return { toon: 'rustig', tekst: 'wacht op de eerste ronde' };
  return { toon: 'rustig', tekst: 'loopt' };
}

/**
 * Per zoekopdracht de namen van de andere actieve zoekopdrachten waarmee hij in
 * gebied overlapt. Een woning hoort bij de feed die hem het laatst zag, dus twee
 * feeds over dezelfde buurt laten hem heen en weer springen - dat hoort op het
 * scherm te staan, bij allebei.
 */
export function overlapPerZoekopdracht(lijst: readonly Zoekopdracht[]): Map<string, string[]> {
  const uit = new Map<string, string[]>();
  for (const een of lijst) {
    if (!een.actief) continue;
    const namen: string[] = [];
    for (const ander of lijst) {
      if (ander.id === een.id || !ander.actief) continue;
      if (overlappendeGebieden(een.gebieden, ander.gebieden).length > 0) namen.push(ander.naam);
    }
    if (namen.length > 0) uit.set(een.id, namen);
  }
  return uit;
}

/** Slug -> naam zoals Funda hem schrijft, voor `gebiedLabel`. */
export function gebiedNamen(gebieden: readonly Gebied[] | undefined): Map<string, string> {
  return new Map((gebieden ?? []).map((gebied) => [gebied.slug, gebied.naam]));
}

/**
 * De keuzes voor de buurtkiezer: Funda's lijst, aangevuld met slugs die al in
 * een zoekopdracht staan of al gekozen zijn maar niet in de lijst voorkomen -
 * die lijst is leeg tot de eerste ronde hem heeft binnengebracht, en dan moet je
 * nog altijd kunnen kiezen wat er al is.
 */
export function kiesOpties(
  gebieden: readonly Gebied[] | undefined,
  bekendeSlugs: readonly string[],
  label: (slug: string) => string,
): Gebied[] {
  const perSlug = new Map<string, Gebied>();
  for (const gebied of gebieden ?? []) perSlug.set(gebied.slug, gebied);
  for (const slug of bekendeSlugs) {
    if (!perSlug.has(slug))
      perSlug.set(slug, { slug, naam: label(slug), soort: null, ouder: null });
  }
  return [...perSlug.values()].sort((a, b) => {
    const stadA = a.soort === 'city' ? 0 : 1;
    const stadB = b.soort === 'city' ? 0 : 1;
    return stadA - stadB || a.naam.localeCompare(b.naam, 'nl');
  });
}
